import { DiscountGroup } from '@retail/products/types';
import { CampaignPrice } from './CampaignPrice';
import { CampaignUser } from './CampaignUser';
import { BaseCampaignPatchItemDTO } from '../dto';
import { calculateSalesCoverageFromCostAndSalesPrice } from '@retail/utils';

export interface BaseCampaignItemProps {
  id: string;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  mgSupplierId: number;
  mgSupplierName: string;
  finfoNumber?: number;
  nobbNumber?: number;
  groupNumber?: string;
  groupDescription?: string;
  discountGroup?: DiscountGroup;
  primaryPackageUnit: string;
  campaignUnit: string;
  campaignSalesUnitPrices: CampaignPrice[];
  campaignPurchaseUnitPrices?: CampaignPrice[];
  isSalesPriceOverridable: boolean;
  purchasePeriodFrom?: string;
  purchasePeriodTo?: string;
  createdAt: string;
  createdBy?: CampaignUser;
}

export abstract class BaseCampaignItem {
  id: string;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  mgSupplierId: number;
  mgSupplierName: string;
  finfoNumber?: number;
  nobbNumber?: number;
  groupNumber?: string;
  groupDescription?: string;
  discountGroup?: DiscountGroup;
  primaryPackageUnit: string;
  campaignUnit: string;
  campaignSalesUnitPrices: CampaignPrice[];
  campaignPurchaseUnitPrices?: CampaignPrice[];
  isSalesPriceOverridable: boolean;
  purchasePeriodFrom?: string;
  purchasePeriodTo?: string;
  createdAt: string;
  createdBy?: CampaignUser;

  constructor({
    id,
    mgItemNumber,
    mgSupplierItemId,
    primaryText,
    secondaryText,
    mgSupplierId,
    mgSupplierName,
    finfoNumber,
    nobbNumber,
    groupNumber,
    groupDescription,
    discountGroup,
    campaignSalesUnitPrices,
    primaryPackageUnit,
    campaignUnit,
    campaignPurchaseUnitPrices,
    isSalesPriceOverridable,
    purchasePeriodFrom,
    purchasePeriodTo,
    createdAt,
    createdBy,
  }: BaseCampaignItemProps) {
    this.id = id;
    this.mgItemNumber = mgItemNumber;
    this.mgSupplierItemId = mgSupplierItemId;
    this.primaryText = primaryText;
    this.secondaryText = secondaryText;
    this.mgSupplierId = mgSupplierId;
    this.mgSupplierName = mgSupplierName;
    this.finfoNumber = finfoNumber;
    this.nobbNumber = nobbNumber;
    this.groupNumber = groupNumber;
    this.groupDescription = groupDescription;
    this.discountGroup = discountGroup;
    this.campaignSalesUnitPrices = campaignSalesUnitPrices;
    this.primaryPackageUnit = primaryPackageUnit;
    this.campaignUnit = campaignUnit;
    this.campaignPurchaseUnitPrices = campaignPurchaseUnitPrices;
    this.isSalesPriceOverridable = isSalesPriceOverridable;
    this.purchasePeriodFrom = purchasePeriodFrom;
    this.purchasePeriodTo = purchasePeriodTo;
    this.createdAt = createdAt;
    this.createdBy = createdBy;
  }

  abstract getSalesPriceForCampaignUnit: () => CampaignPrice | undefined;

  getPurchasePriceForCampaignUnit = () =>
    this.campaignPurchaseUnitPrices?.find(
      (price: CampaignPrice) => price.unit.unit === this.campaignUnit
    );

  getCampaignSalesCoverage = (): number | undefined => {
    const localCostPrice = this.getPurchasePriceForCampaignUnit();
    const localSalesPrice = this.getSalesPriceForCampaignUnit();

    return localCostPrice && localSalesPrice
      ? calculateSalesCoverageFromCostAndSalesPrice(
          localCostPrice.priceWithoutVat,
          localSalesPrice.priceWithoutVat
        )
      : undefined;
  };

  getFormattedCampaignSalesCoverage = (): string | undefined => {
    const salesCoverage = this.getCampaignSalesCoverage();
    return !isNaN(Number(salesCoverage)) ? `${Number(salesCoverage!.toFixed(2))}%` : undefined;
  };

  protected getBasePatchCampaignItem = (): BaseCampaignPatchItemDTO => ({
    campaignUnit: this.campaignUnit,
    campaignSalesPrice: this.getSalesPriceForCampaignUnit()?.priceWithVat || 0,
    campaignPurchasePrice: this.getPurchasePriceForCampaignUnit()?.priceWithoutVat,
    purchasePeriodFrom: this.purchasePeriodFrom,
    purchasePeriodTo: this.purchasePeriodTo,
  });

  abstract getPatchCampaignItem: () => BaseCampaignPatchItemDTO;
}
